(() => {
  if (customElements.get('campaign-video')) return;
  const tiles = new Map();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const startupTimeout = 5000;
  const bufferingTimeout = 2000;
  let videoId = 0;

  function allowAutoplay() {
    return !motion.matches && !connection?.saveData && !['slow-2g', '2g'].includes(connection?.effectiveType);
  }

  class CampaignVideo {
    constructor(tile) {
      this.tile = tile;
      this.video = tile.querySelector('video[data-src]');
      this.button = tile.querySelector('[data-campaign-video-toggle]');
      this.visible = false;
      this.userPaused = false;
      this.manualOptIn = false;
      this.failed = false;
      this.playAttempt = 0;
      this.playPending = false;
      this.listeners = [];
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.playsInline = true;
      if (!this.video.id) this.video.id = `campaign-video-${++videoId}`;

      this.on(this.video, 'playing', () => {
        if (!this.shouldPlay()) {
          this.pause();
          return;
        }
        this.clearTimers();
        this.tile.classList.add('campaign-video-is-playing');
        this.updateButton();
      });
      this.on(this.video, 'waiting', () => this.waitForBuffer());
      this.on(this.video, 'stalled', () => {
        if (this.video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) this.waitForBuffer();
      });
      this.on(this.video, 'error', () => this.fallback());
      this.on(this.video, 'pause', () => this.updateButton());

      if (this.button) {
        this.button.hidden = false;
        this.button.setAttribute('aria-controls', this.video.id);
        this.on(this.button, 'click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (!this.video.paused || this.playPending) {
            this.userPaused = true;
            this.pause();
          } else {
            this.manualOptIn = true;
            this.userPaused = false;
            const rect = this.tile.getBoundingClientRect();
            this.visible = rect.bottom > 0 && rect.top < window.innerHeight;
            this.sync();
          }
          this.updateButton();
        });
      }
      this.updateButton();
    }

    on(element, event, callback) {
      element.addEventListener(event, callback);
      this.listeners.push([element, event, callback]);
    }

    shouldPlay() {
      return this.visible && !document.hidden && !this.userPaused && !this.failed &&
        (this.manualOptIn || allowAutoplay());
    }

    clearTimers() {
      clearTimeout(this.startupTimer);
      clearTimeout(this.bufferTimer);
      this.startupTimer = null;
      this.bufferTimer = null;
    }

    pause() {
      this.playAttempt += 1;
      this.playPending = false;
      this.clearTimers();
      this.video.pause();
      this.updateButton();
    }

    sync() {
      if (!this.shouldPlay()) {
        this.pause();
        if (!this.manualOptIn && !allowAutoplay()) {
          this.tile.classList.remove('campaign-video-is-playing');
        }
        return;
      }
      if (this.playPending || !this.video.paused) return;
      if (!this.video.getAttribute('src')) this.video.src = this.video.dataset.src;
      this.playPending = true;
      const attempt = ++this.playAttempt;
      this.startupTimer = setTimeout(() => {
        if (attempt === this.playAttempt && this.shouldPlay()) this.fallback();
      }, startupTimeout);
      try {
        Promise.resolve(this.video.play()).then(() => {
          if (attempt !== this.playAttempt) return;
          this.playPending = false;
          if (!this.shouldPlay()) this.pause();
          this.updateButton();
        }).catch(() => {
          if (attempt !== this.playAttempt || !this.shouldPlay()) return;
          this.fallback();
        });
      } catch (_) {
        this.fallback();
      }
      this.updateButton();
    }

    waitForBuffer() {
      if (!this.shouldPlay() || this.bufferTimer) return;
      this.bufferTimer = setTimeout(() => {
        if (this.shouldPlay() && this.video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) {
          this.fallback();
        }
      }, bufferingTimeout);
    }

    fallback() {
      if (this.failed) return;
      this.failed = true;
      this.pause();
      this.tile.classList.remove('campaign-video-is-playing');
      this.video.removeAttribute('src');
      this.video.load();
      if (this.button) this.button.hidden = true;
    }

    updateButton() {
      if (!this.button) return;
      const playing = !this.video.paused || this.playPending;
      const label = playing
        ? this.button.dataset.labelPause
        : this.button.dataset.labelPlay;
      this.button.setAttribute('aria-label', label);
      this.button.setAttribute('title', label);
      this.button.dataset.state = playing ? 'playing' : 'paused';
    }

    preferenceChanged() {
      this.manualOptIn = false;
      this.sync();
      if (!allowAutoplay() && this.video.hasAttribute('src')) {
        this.video.removeAttribute('src');
        this.video.load();
      }
    }

    destroy() {
      this.failed = true;
      this.pause();
      this.listeners.forEach(([element, event, callback]) => element.removeEventListener(event, callback));
      this.video.removeAttribute('src');
      this.video.load();
      observer?.unobserve(this.tile);
      tiles.delete(this.tile);
    }
  }

  const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const controller = tiles.get(entry.target);
      if (!controller) return;
      controller.visible = entry.isIntersecting && entry.intersectionRatio >= 0.1;
      controller.sync();
    });
  }, { threshold: [0, 0.1] }) : null;

  class CampaignVideoTile extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected || tiles.has(this) || !this.querySelector('video[data-src]')) return;
        const controller = new CampaignVideo(this);
        tiles.set(this, controller);
        observer?.observe(this);
      });
    }

    disconnectedCallback() {
      tiles.get(this)?.destroy();
    }
  }

  document.addEventListener('visibilitychange', () => tiles.forEach((controller) => controller.sync()));
  const preferenceChanged = () => tiles.forEach((controller) => controller.preferenceChanged());
  if (motion.addEventListener) motion.addEventListener('change', preferenceChanged);
  else motion.addListener(preferenceChanged);
  connection?.addEventListener?.('change', preferenceChanged);
  customElements.define('campaign-video', CampaignVideoTile);
})();
